import React from 'react';
import PropTypes from 'prop-types';
import { Debug } from '@svt/debug';
import { useQuery } from 'urql';
import get from 'app/utils/get';

const { error: errorLogger } = Debug('play:ContentoQuery:error');

const DEFAULT_APOLLO_QUERY_TIMEOUT = 15000;

const ContentoDisplayNames = {
    LOADING: 'ContentoLoading',
    ERROR: 'ContentoError',
    DATA: 'ContentoData',
};

const ContentoLoading = ({ children }) => children;
ContentoLoading.displayName = ContentoDisplayNames.LOADING;

const ContentoError = ({ children, error }) => {
    if (typeof children === 'function') {
        return children(error);
    }

    return React.cloneElement(children, { error });
};
ContentoError.displayName = ContentoDisplayNames.ERROR;

const ContentoData = ({ children, data }) => {
    if (typeof children === 'function') {
        return children(data);
    }

    return React.cloneElement(children, { data });
};
ContentoData.displayName = ContentoDisplayNames.DATA;

//find react child with specific display name
const findChildByName = (children, name) => {
    let found = null;
    React.Children.forEach(children, (child) => {
        if (child.type.displayName === name) {
            found = child;
        }
    });

    return found;
};

/**
 * ContentoQuery
 *
 * This approach aim for a flexible way to render the different states a query can be in.
 * Each state (Loading, Error, Data) have corresponding sub component that in a declarative way can be used
 * to structure a query. The good thing with using sub components is that we can decide which state, or states,
 * we want to render just by adding a sub component matching the state. For example if we want to show Loading state and
 * Data state but ignore Error state we just provide components for the the first two. IE:
 *
 * `
 *  <ContentoQuery ...>
 *      <ContentoLoading>
 *          <div>
 *              Loader...
 *          </div>
 *      </ContentoLoading>
 *      <ContentoData>
 *          <DisplayWindow .../>
 *      </ContentoData>
 *  </ContentoQuery>
 * `
 *
 * If you are using components the data will injected into to those. When using the `ContentData` component (or no sub components at all) the child
 * will get the query response as `contentoData` prop. And the `ContentoError` component will get a `contentoError` prop.
 *
 * LOADING
 *
 * Use `ContentoLoading` component to show something when fetching data
 *
 * DATA
 *
 * Use `ContentoData` component to display the data. You can use components (that will get the `contentoData` prop injected) or you can use render props:
 *
 * `
 * <ContentoQuery ...>
 *      <ContentoData>
 *         {(contentoData) => {
 *              //use something with the data
 *              const items = parseData(contentoData);
 *
 *              return <DisplayWindow items={items} />;
 *         }}
 *      </ContentoData>
 *  </ContentoQuery>
 *`
 *
 * ERROR`
 *
 * Use `ContentoError` component to show render something when error is found. You can use components (that will get the `contentoError` prop injected) or you can use render props:
 *
 * `
 * <ContentoQuery ...>
 *      <ContentoError>
 *         {(contentoError) => {
 *              //use something with the data
 *              const msg = contentoError.message;
 *
 *              return <div>{ msg }</div>;
 *         }}
 *      </ContentoError>
 *  </ContentoQuery>
 *`
 * @returns a ApolloQuery with pre defined data responses
 */

const getErrorComponent = (children, error) => {
    // if a `ContentoError` component is provided, return that, otherwise null
    const ErrorComponent = findChildByName(children, ContentoDisplayNames.ERROR);
    if (ErrorComponent) {
        return React.cloneElement(ErrorComponent, { contentoError: error });
    }

    return null;
};

const getDataComponent = (children, data) => {
    const DataComponent = findChildByName(children, ContentoDisplayNames.DATA);
    if (DataComponent) {
        return React.cloneElement(DataComponent, { data });
    }

    //if no specific contento sub component is present just take the child component and wrap it in a `ContentoData` component
    return <ContentoData data={data}>{children}</ContentoData>;
};

export function renderContentoQuery(loading, error, data, children, isLoadingScreenEnabled, queryName) {
    if (error) {
        errorLogger(
            'Query %s returned error : graphQLErrors=%s networkError=%s message=%s data=%s',
            queryName,
            error.graphQLErrors.join(', '),
            error.networkError,
            error.message,
            JSON.stringify(data)
        );

        return getErrorComponent(children, error);
    }

    if (isLoadingScreenEnabled && loading && !data) {
        return findChildByName(children, ContentoDisplayNames.LOADING);
    }

    return data ? getDataComponent(children, data) : null;
}

const ContentoQuery = ({
    fetchPolicy = 'cache-first',
    children,
    isLoadingScreenEnabled = true,
    query,
    variables,
    preferGetMethod = true,
}) => {
    const queryName = get(query, 'definitions[0].name.value', 'Unknown');

    const [result] = useQuery({
        query,
        variables,
        requestPolicy: fetchPolicy,
        context: React.useMemo(
            () => ({
                preferGetMethod,
            }),
            [preferGetMethod]
        ),
    });

    const { data, fetching, error } = result;

    // console.log('query', { queryName, data: typeof data, fetching, error });

    return renderContentoQuery(fetching, error, data, children, isLoadingScreenEnabled, queryName);
};

ContentoQuery.propTypes = {
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.array]).isRequired,
    fetchPolicy: PropTypes.string,
    isLoadingScreenEnabled: PropTypes.bool,
};

export { ContentoQuery, ContentoLoading, ContentoData, ContentoError, DEFAULT_APOLLO_QUERY_TIMEOUT };

export default ContentoQuery;
